import axios from 'axios';

// The Customer portal's own API client and session storage (issue #254).
// A separate axios instance, so the staff interceptors in axiosAuth.js (the
// staff session, the shared app password, the staff signed-out event) never
// touch a portal request. The portal session travels in x-portal-session
// (backend/controllers/portalAuthController.js), stored per portal address
// so signing in at one tenant's portal never signs anyone in at another's.
//
// A Login Switcher tab (issue #283) holds a switch token instead, in
// sessionStorage so it lives only in that tab, sent as x-portal-switch. While
// a tab holds one it never sends (or touches) a real portal session.

export const portalApi = axios.create({ baseURL: process.env.REACT_APP_API_URL || '' });

const storageKey = (portalName) => `optim_portal_session:${portalName}`;
const switchKey = (portalName) => `optim_portal_switch:${portalName}`;

export function getPortalSession(portalName) {
  try {
    return window.localStorage.getItem(storageKey(portalName));
  } catch {
    return null;
  }
}

export function setPortalSession(portalName, token) {
  try {
    if (!token) window.localStorage.removeItem(storageKey(portalName));
    else window.localStorage.setItem(storageKey(portalName), token);
  } catch {
    // Storage blocked — the session just won't survive a reload.
  }
}

// The in-memory copy covers storage being blocked, for the life of the page.
const switchTokens = {};

export function getPortalSwitch(portalName) {
  try {
    return window.sessionStorage.getItem(switchKey(portalName)) || switchTokens[portalName] || null;
  } catch {
    return switchTokens[portalName] || null;
  }
}

export function setPortalSwitch(portalName, token) {
  if (token) switchTokens[portalName] = token;
  else delete switchTokens[portalName];
  try {
    if (!token) window.sessionStorage.removeItem(switchKey(portalName));
    else window.sessionStorage.setItem(switchKey(portalName), token);
  } catch {
    // Storage blocked — kept in memory only.
  }
}

// The back-office page a switch tab was opened from, kept after the switch
// ends so the tab can go back to it. Only a same-site path outside the
// portal is accepted, so the link can't send the tab anywhere else.
const switchReturnKey = (portalName) => `optim_portal_switch_return:${portalName}`;

export function isSwitchReturnPath(path) {
  return typeof path === 'string' && /^\/(?![/\\])/.test(path) && !/^\/portal(\/|$|\?)/.test(path);
}

export function getSwitchReturn(portalName) {
  try {
    const path = window.sessionStorage.getItem(switchReturnKey(portalName));
    return isSwitchReturnPath(path) ? path : '/';
  } catch {
    return '/';
  }
}

export function setSwitchReturn(portalName, path) {
  try {
    if (isSwitchReturnPath(path)) window.sessionStorage.setItem(switchReturnKey(portalName), path);
  } catch {
    // Storage blocked — the tab goes back to the back office home page.
  }
}

export function sessionHeaders(portalName) {
  const switchToken = getPortalSwitch(portalName);
  if (switchToken) return { 'x-portal-switch': switchToken };
  const token = getPortalSession(portalName);
  return token ? { 'x-portal-session': token } : {};
}

// Saves a PDF response as a file in the browser.
export function saveBlob(data, filename) {
  const url = window.URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => window.URL.revokeObjectURL(url), 1000);
}

export function portalErrorMessage(err, fallback) {
  if (!err.response) return 'Could not reach the server. It may be starting up, so try again in a moment.';
  return err.response.data?.message || fallback;
}
